/**
 * "Staff the weekend" — Easy button #1 (memory-bank/plans/easy-buttons.md).
 *
 * Opens with a plan already proposed from server defaults. A coordinator who
 * has never seen it should be able to read one line and press Assign.
 *
 * Why this one is EDITABLE and autoplan is not
 * --------------------------------------------
 * Autoplan's commit re-plans from the request body, so its preview is a
 * read-only forecast — right for autoplan, wrong here. Staffing is a judgement
 * call ("not Dana, she had the hard turf last week"), so the proposal is a
 * starting point and the coordinator overrides rows directly. The commit then
 * writes exactly the map on screen.
 *
 * Each row's dropdown offers only `eligible_canvasser_ids`, which the server
 * computed from that canvasser's region scope. The UI never presents an option
 * the commit would reject, and every edit is re-validated server-side anyway —
 * the dropdown is a convenience, not the security boundary.
 *
 * Styling follows AutoplanRoutesModal's idiom (semantic tokens, right-side
 * panel, "More options" fold).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Users,
} from 'lucide-react';

import {
  previewStaffing,
  commitStaffing,
  easyButtonError,
  type StaffingPreviewResponse,
  type StaffingPreviewRequest,
} from '@/api/easy';
import ProposalLedger from '@/components/easy/ProposalLedger';

const n = (v: number) => v.toLocaleString();

function isAbortError(err: any): boolean {
  return (
    err?.name === 'CanceledError' ||
    err?.name === 'AbortError' ||
    err?.code === 'ERR_CANCELED'
  );
}

type Props = {
  onClose: () => void;
  /** Called after a successful commit so the list can refresh. */
  onStaffed: (assigned: number, shifts: number) => void;
};

export default function StaffWeekendModal({ onClose, onStaffed }: Props) {
  const [limit, setLimit] = useState(20);
  const [balance, setBalance] = useState<'round_robin' | 'by_doors'>('round_robin');
  const [includeAssigned, setIncludeAssigned] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const [createShifts, setCreateShifts] = useState(false);
  const [shiftDate, setShiftDate] = useState('');

  /**
   * The coordinator's overrides, route_id -> canvasser_id (0 = leave open).
   * Kept separate from the server's proposal so a re-preview (after changing a
   * knob) re-balances the untouched rows while honouring the edited ones —
   * that is why the edits are POSTed back rather than merged client-side.
   */
  const [edits, setEdits] = useState<Record<number, number>>({});

  const [preview, setPreview] = useState<StaffingPreviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputCls =
    'w-full rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-accent';

  const params = useMemo<StaffingPreviewRequest>(
    () => ({
      limit,
      balance,
      include_assigned: includeAssigned,
      assignments: Object.keys(edits).length ? edits : undefined,
    }),
    [limit, balance, includeAssigned, edits],
  );

  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const t = setTimeout(() => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      previewStaffing(params, ctrl.signal)
        .then((res) => {
          setPreview(res);
          setError(null);
        })
        .catch((err) => {
          if (isAbortError(err)) return;
          setError(easyButtonError(err, 'Could not work out a staffing plan.'));
        })
        .finally(() => {
          if (!ctrl.signal.aborted) setLoading(false);
        });
    }, 250);
    return () => clearTimeout(t);
  }, [params]);

  const setRow = useCallback((routeId: number, canvasserId: number) => {
    setEdits((prev) => ({ ...prev, [routeId]: canvasserId }));
  }, []);

  const handleCommit = useCallback(async () => {
    if (!preview) return;
    const map: Record<number, number> = {};
    for (const [rid, cid] of Object.entries(preview.assignments)) {
      map[Number(rid)] = Number(cid);
    }
    if (!Object.keys(map).length) return;
    setCommitting(true);
    setError(null);
    try {
      const res = await commitStaffing(map, {
        create_shifts: createShifts,
        shift_date: createShifts && shiftDate ? new Date(shiftDate).toISOString() : null,
      });
      onStaffed(res.assigned, res.shifts_created);
    } catch (err) {
      setError(easyButtonError(err, 'Could not save the assignments.'));
      setCommitting(false);
    }
  }, [preview, createShifts, shiftDate, onStaffed]);

  const nameFor = useCallback(
    (id: number | null) => {
      if (!id) return null;
      const c = preview?.canvassers.find((x) => x.id === id);
      return c?.full_name || c?.email || `User ${id}`;
    },
    [preview],
  );

  const plannedCount = preview ? Object.keys(preview.assignments).length : 0;
  const noCanvassers = preview !== null && preview.canvassers.length === 0;
  const commitDisabled =
    loading || committing || plannedCount === 0 || (createShifts && !shiftDate);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50">
      <div className="flex h-full w-full max-w-2xl flex-col bg-bg-raised shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-border-subtle p-4">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-base font-semibold text-fg-primary">
              <Users size={18} /> Staff the weekend
            </h2>
            <p className="mt-1 text-sm text-fg-secondary">
              Hands your open routes to canvassers, biggest turf first. Change
              anyone you like before saving — nothing is written until you press
              Assign.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-fg-muted hover:bg-bg-base hover:text-fg-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {error && (
            <div className="flex items-start gap-2 rounded-md border border-status-danger/40 bg-status-danger/10 p-3 text-sm text-status-danger">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {noCanvassers && (
            <div className="rounded-md border border-border-subtle bg-bg-base p-3 text-sm text-fg-secondary">
              There are no active canvassers in this tenant yet, so there is
              nobody to assign routes to. Add canvassers under{' '}
              <span className="font-medium text-fg-primary">Admin → Users</span>{' '}
              first.
            </div>
          )}

          {preview && <ProposalLedger
            proposal={preview.proposal}
            noun="routes"
            plannedVerb="will be assigned"
          />}

          {/* Per-canvasser load. An unfair split has to be visible BEFORE it is
              written — that is most of why this modal exists rather than a
              plain button. */}
          {preview && preview.canvassers.some((c) => c.routes > 0) && (
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                Who gets what
              </h3>
              <div className="space-y-1">
                {preview.canvassers
                  .filter((c) => c.routes > 0)
                  .map((c) => (
                    <div
                      key={c.id}
                      className="flex items-baseline justify-between gap-3 rounded border border-border-subtle bg-bg-base px-3 py-2 text-sm"
                    >
                      <span className="truncate text-fg-primary">
                        {c.full_name || c.email}
                        {c.scoped && (
                          <span className="ml-2 rounded bg-bg-raised px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-fg-muted">
                            scoped
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 tabular-nums text-fg-secondary">
                        {c.routes} {c.routes === 1 ? 'route' : 'routes'} ·{' '}
                        {n(c.doors)} doors
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* The editable plan */}
          {preview && preview.proposal.items.length > 0 && (
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                The plan — change anyone
              </h3>
              <div className="space-y-1">
                {preview.proposal.items.map((item) => (
                  <div
                    key={item.route_id}
                    className="flex items-center gap-3 rounded border border-border-subtle bg-bg-base px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-fg-primary">
                        {item.route_name}
                      </div>
                      <div className="text-xs text-fg-muted">
                        {item.door_count} doors
                        {item.precinct_num ? ` · precinct ${item.precinct_num}` : ''}
                        {item.currently_assigned_to
                          ? ` · currently ${nameFor(item.currently_assigned_to)}`
                          : ''}
                      </div>
                    </div>
                    <select
                      value={item.assigned_to ?? 0}
                      onChange={(e) => setRow(item.route_id, Number(e.target.value))}
                      className="w-44 shrink-0 rounded-md border border-border-subtle bg-bg-raised px-2 py-1.5 text-sm text-fg-primary focus:outline-none focus:ring-2 focus:ring-accent"
                      aria-label={`Canvasser for ${item.route_name}`}
                    >
                      <option value={0}>— leave open —</option>
                      {/* Server-computed: only canvassers whose scope covers
                          this route. */}
                      {item.eligible_canvasser_ids.map((cid) => (
                        <option key={cid} value={cid}>
                          {nameFor(cid)}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {preview && preview.proposal.items.length === 0 && !noCanvassers && (
            <div className="rounded-md border border-border-subtle bg-bg-base p-3 text-sm text-fg-secondary">
              Every route in your turf already has a canvasser. Turn on{' '}
              <span className="font-medium text-fg-primary">
                re-shuffle assigned routes
              </span>{' '}
              under More options to redo a weekend.
            </div>
          )}

          {/* More options */}
          <div>
            <button
              type="button"
              onClick={() => setShowOptions((v) => !v)}
              className="flex items-center gap-1 text-sm text-fg-secondary hover:text-fg-primary"
            >
              {showOptions ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              More options
            </button>

            {showOptions && (
              <div className="mt-3 space-y-3 rounded-md border border-border-subtle bg-bg-base p-3">
                <div>
                  <label
                    htmlFor="sw-limit"
                    className="mb-1 block text-xs font-semibold text-fg-secondary"
                  >
                    How many routes to staff
                  </label>
                  <input
                    id="sw-limit"
                    type="number"
                    min={1}
                    max={500}
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value) || 1)}
                    className={inputCls}
                  />
                </div>

                <div>
                  <label
                    htmlFor="sw-balance"
                    className="mb-1 block text-xs font-semibold text-fg-secondary"
                  >
                    How to split them
                  </label>
                  <select
                    id="sw-balance"
                    value={balance}
                    onChange={(e) => setBalance(e.target.value as any)}
                    className={inputCls}
                  >
                    <option value="round_robin">Take turns (biggest turf first)</option>
                    <option value="by_doors">Even out the door counts</option>
                  </select>
                </div>

                <label className="flex items-start gap-2 text-xs text-fg-secondary">
                  <input
                    type="checkbox"
                    checked={includeAssigned}
                    onChange={(e) => {
                      setIncludeAssigned(e.target.checked);
                      setEdits({});
                    }}
                    className="mt-0.5"
                  />
                  <span>
                    Re-shuffle routes that already have a canvasser. Off by
                    default, which is what makes pressing this again next
                    weekend staff only what is still open.
                  </span>
                </label>

                <label className="flex items-start gap-2 text-xs text-fg-secondary">
                  <input
                    type="checkbox"
                    checked={createShifts}
                    onChange={(e) => setCreateShifts(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>Also create a planned shift for each assignment</span>
                </label>

                {createShifts && (
                  <div>
                    <label
                      htmlFor="sw-date"
                      className="mb-1 block text-xs font-semibold text-fg-secondary"
                    >
                      Shift date <span className="text-status-danger">*</span>
                    </label>
                    <input
                      id="sw-date"
                      type="date"
                      value={shiftDate}
                      onChange={(e) => setShiftDate(e.target.value)}
                      className={inputCls}
                    />
                    <p className="mt-1 text-xs text-fg-muted">
                      Required — a planned shift with no date is a row nobody
                      will find.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-border-subtle p-4">
          <span className="text-sm text-fg-secondary">
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Working it out…
              </span>
            ) : (
              `${n(plannedCount)} ${plannedCount === 1 ? 'route' : 'routes'} ready to assign`
            )}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border-subtle px-3 py-2 text-sm text-fg-primary hover:bg-bg-base"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCommit}
              disabled={commitDisabled}
              className="inline-flex items-center gap-2 rounded-md bg-accent px-3 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {committing && <Loader2 size={14} className="animate-spin" />}
              Assign {plannedCount > 0 ? n(plannedCount) : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
